import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import toast from 'react-hot-toast';

export interface User {
    id: string;
    email: string;
    role: 'warehouse_manager' | 'warehouse_staff' | 'carrier' | 'admin';
    warehouseId?: string;
    firstName?: string;
    lastName?: string;
    companyName?: string;
    mcNumber?: string;
    phone?: string;
    isOnboarded?: boolean;
    isActive?: boolean;
    timeZone?: string;
    trialStatus?: string | null;  // trialing | active | expired
    trialEndAt?: string | null;   // ISO string
    plan?: string | null;
    subscriptionStatus?: string | null;
    effectiveTier?: 'none' | 'starter' | 'operations';
    isAdmin?: boolean;
}

export interface UserPreferences {
    weightUnit: 'LBS' | 'KG';
    distanceUnit: 'MI' | 'KM';
    gracePeriod: number; // minutes
    detentionRate: number; // dollars per hour
}

export interface LogoutOptions {
    /**
     * Set true when the server has ALREADY rejected the token with 401 Unauthorized
     * so client state is cleared immediately without issuing a redundant /logout request.
     */
    skipServerRevoke?: boolean;
    /** Suppress error toast when network/server revocation fails. */
    silent?: boolean;
}

interface AuthState {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    location: { lat: number; lng: number } | null;
    hasLocationConsent: boolean;
    lastLogoutError: string | null;
    // Phase 10: Preferences
    preferences: UserPreferences;
    login: (user: User, token: string) => void;
    logout: (options?: LogoutOptions) => Promise<boolean>;
    updateUser: (user: User) => void;
    setLocation: (lat: number, lng: number) => void;
    setConsent: (hasConsent: boolean) => void;
    updatePreferences: (prefs: Partial<UserPreferences>) => void;
    refreshSession: () => Promise<void>;
    hasHydrated: boolean;
    setHasHydrated: (state: boolean) => void;
}

const REVOKED_SESSION_SIGNAL_KEY = 'baywhiz_revoked_session_signal_v1';

/**
 * Derives a non-sensitive, one-way 64-bit FNV-1a correlation digest (`sid_<16-hex>`)
 * for cross-tab logout signaling so raw JWTs or JWT substrings are never broadcast via localStorage.
 */
export const computeSessionSignalId = (rawToken: string): string => {
    let h1 = 0x811c9dc5;
    let h2 = 0x050c5d1f;
    for (let i = 0; i < rawToken.length; i++) {
        const ch = rawToken.charCodeAt(i);
        h1 ^= ch;
        h1 = Math.imul(h1, 0x01000193) >>> 0;
        h2 ^= ch ^ (i & 0xff);
        h2 = Math.imul(h2, 0x01000193) >>> 0;
    }
    return `sid_${h1.toString(16).padStart(8, '0')}${h2.toString(16).padStart(8, '0')}`;
};

const clearLocalAuthStorage = (revokedToken?: string | null) => {
    if (typeof window === 'undefined') return;
    try {
        if (revokedToken) {
            const sessionId = computeSessionSignalId(revokedToken);
            localStorage.setItem(
                REVOKED_SESSION_SIGNAL_KEY,
                JSON.stringify({ sessionId, ts: Date.now() })
            );
            localStorage.removeItem(REVOKED_SESSION_SIGNAL_KEY);
        }
        localStorage.removeItem('authToken');
        localStorage.removeItem('token');
    } catch {
        // Ignore storage write errors
    }
};

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            user: null,
            token: null,
            isAuthenticated: false,
            location: null,
            hasLocationConsent: false,
            lastLogoutError: null,
            // Default Preferences
            preferences: {
                weightUnit: 'LBS',
                distanceUnit: 'MI',
                gracePeriod: 15,
                detentionRate: 75
            },
            login: (user, token) => {
                if (typeof window !== 'undefined') {
                    localStorage.setItem('authToken', token);
                    localStorage.removeItem('token');
                }
                set({ user, token, isAuthenticated: true, lastLogoutError: null });
            },
            logout: async (options?: LogoutOptions): Promise<boolean> => {
                const { token, user } = get();
                const activeToken =
                    token ||
                    (typeof window !== 'undefined' ? localStorage.getItem('authToken') : null);

                if (activeToken && !options?.skipServerRevoke) {
                    const isCarrier = user?.role === 'carrier';
                    const endpoint = isCarrier ? '/api/carrier/auth/logout' : '/api/auth/logout';
                    try {
                        const res = await fetch(endpoint, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${activeToken}`,
                            },
                        });

                        // 200 OK = newly revoked on server; 401 = token already expired or revoked on server
                        if (!res.ok && res.status !== 401) {
                            const errBody = await res.json().catch(() => ({}));
                            const errMsg =
                                errBody?.error ||
                                'Unable to complete secure sign-out on the server. Please try again.';
                            set({ lastLogoutError: errMsg });
                            if (!options?.silent) {
                                toast.error(errMsg);
                            }
                            return false;
                        }
                    } catch {
                        const errMsg =
                            'Network error while signing out. Your session could not be revoked on the server — please check your connection and try again.';
                        set({ lastLogoutError: errMsg });
                        if (!options?.silent) {
                            toast.error(errMsg);
                        }
                        return false;
                    }
                }

                clearLocalAuthStorage(activeToken);
                set({
                    user: null,
                    token: null,
                    isAuthenticated: false,
                    location: null,
                    hasLocationConsent: false,
                    lastLogoutError: null,
                });
                return true;
            },
            updateUser: (user) => {
                set({ user });
            },
            setLocation: (lat, lng) => {
                set({ location: { lat, lng }, hasLocationConsent: true });
            },
            setConsent: (hasConsent) => {
                set({ hasLocationConsent: hasConsent });
            },
            updatePreferences: (newPrefs) => {
                set((state) => ({
                    preferences: { ...state.preferences, ...newPrefs }
                }));
            },
            refreshSession: async () => {
                // Guard against cross-tab logout resurrection: if another tab cleared auth-storage/authToken, sync logout
                if (typeof window !== 'undefined') {
                    try {
                        const rawPersisted = localStorage.getItem('auth-storage');
                        if (rawPersisted) {
                            const parsed = JSON.parse(rawPersisted);
                            if (parsed?.state && (!parsed.state.isAuthenticated || !parsed.state.token)) {
                                set({ user: null, token: null, isAuthenticated: false, location: null, hasLocationConsent: false });
                                return;
                            }
                        } else if (!localStorage.getItem('authToken')) {
                            set({ user: null, token: null, isAuthenticated: false, location: null, hasLocationConsent: false });
                            return;
                        }
                    } catch {
                        // Ignore storage parse error
                    }
                }
                const { token, user } = useAuthStore.getState();
                if (!token || !user) return;
                try {
                    const res = await fetch('/api/auth/me', {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    if (res.status === 401) {
                        await useAuthStore.getState().logout({ skipServerRevoke: true, silent: true });
                        return;
                    }
                    if (!res.ok) return;
                    const data = await res.json();
                    // Re-verify another tab did not log out or switch tokens while fetch was in flight
                    const latest = useAuthStore.getState();
                    if (!latest.isAuthenticated || latest.token !== token) return;
                    if (data?.user) {
                        // Merge canonical DB state into current user, preserving client-only fields
                        set({
                            user: {
                                ...user,
                                ...data.user,
                            },
                        });
                    }
                } catch { /* silent — network error, keep stale state */ }
            },
            hasHydrated: false,
            setHasHydrated: (state) => {
                set({ hasHydrated: state });
            }
        }),
        {
            name: 'auth-storage',
            onRehydrateStorage: () => (state) => {
                if (state) {
                    state.setHasHydrated(true);
                }
            }
        }
    )
);

// Cross-tab auth synchronization: immediately unauthenticate other tabs sharing the same revoked session
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('storage', (event: StorageEvent) => {
        if (event.key === REVOKED_SESSION_SIGNAL_KEY && event.newValue) {
            try {
                const payload = JSON.parse(event.newValue);
                const current = useAuthStore.getState();
                const currentSessionId = current.token ? computeSessionSignalId(current.token) : null;
                // Only unauthenticate tabs sharing the same non-sensitive session correlation ID
                if (payload?.sessionId && currentSessionId && currentSessionId === payload.sessionId) {
                    useAuthStore.setState({
                        user: null,
                        token: null,
                        isAuthenticated: false,
                        location: null,
                        hasLocationConsent: false,
                    });
                }
            } catch {
                // Ignore malformed signal
            }
            return;
        }
        if (event.key === 'authToken' && !event.newValue) {
            const current = useAuthStore.getState();
            if (!event.oldValue || current.token === event.oldValue) {
                if (current.isAuthenticated || current.token) {
                    useAuthStore.setState({
                        user: null,
                        token: null,
                        isAuthenticated: false,
                        location: null,
                        hasLocationConsent: false,
                    });
                }
            }
            return;
        }
        if (event.key === 'auth-storage') {
            if (!event.newValue) {
                useAuthStore.setState({
                    user: null,
                    token: null,
                    isAuthenticated: false,
                    location: null,
                    hasLocationConsent: false,
                });
                return;
            }
            try {
                const parsed = JSON.parse(event.newValue);
                const nextState = parsed?.state;
                if (nextState && (!nextState.isAuthenticated || !nextState.token)) {
                    const current = useAuthStore.getState();
                    let oldToken: string | undefined;
                    if (event.oldValue) {
                        try {
                            oldToken = JSON.parse(event.oldValue)?.state?.token;
                        } catch {
                            oldToken = undefined;
                        }
                    }
                    if (!oldToken || current.token === oldToken) {
                        if (current.isAuthenticated || current.token) {
                            useAuthStore.setState({
                                user: null,
                                token: null,
                                isAuthenticated: false,
                                location: null,
                                hasLocationConsent: false,
                            });
                        }
                    }
                } else if (nextState?.isAuthenticated && nextState?.token && nextState?.user) {
                    const current = useAuthStore.getState();
                    if (!current.isAuthenticated && current.token !== nextState.token) {
                        useAuthStore.setState({
                            user: nextState.user,
                            token: nextState.token,
                            isAuthenticated: true,
                        });
                    }
                }
            } catch {
                // Ignore malformed storage payload
            }
        }
    });
}
